import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import { ApolloError } from '@apollo/client';

import {
  Button,
  CircularProgress,
  ContentCopyOutlinedIcon,
  EditIcon,
  LinearProgress,
  Typography,
  VisibilityOutlinedIcon,
  ArchiveIcon,
  MessageIcon,
  ConfirmDialog,
  useConfirmDialog,
} from '@occc/ui-design-system';
import {
  OfferSupportAlert,
  Page,
  useAppLayoutContextConsumer,
  NotFoundPage,
  snackbar,
} from '@occc-app/design-system';
import { getOfferComponentType } from '@occc-app/offerHelpers/utils';
import { useTriggerVisibilityNotificationsMutation } from '@occc/graphql';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { resolveOfferEditUrl } from '@occc-app/routes/OfferRedirect';

import { ComponentDetails } from './component-details.component';
import { Offer } from './component-details.utils';
import { ConstraintSupplementalData } from './component-details.types';
import { PromocodesSection } from './sections';
import { CampaignComponentCopyDialogFormValues } from '../components/CampaignComponentCopyDialog/types';
import { CampaignComponentConfirmCopyDialog } from '../components/CampaignComponentCopyDialog';
import { ArchiveConfirmDialog } from '../components/ArchiveConfirmDialog';

import { getDetailsPageLinks } from '../utils';

type ComponentDetailsPageProps = {
  campaign?: {
    uuid: string;
    name: string;
  } | null;
  offer?: Offer | null;
  constraintSupplementalData?: ConstraintSupplementalData;
  loading: boolean;
  initiallyLoading: boolean;
  isEditAvailable: boolean;
  isCopyAvailable: boolean;
  isCopyInProgress: boolean;
  onCopyDialogOpen: () => void;
  onCopyDialogClose: () => void;
  isCopyDialogOpen: boolean;
  error?: ApolloError;
  onOfferCopy: (values: CampaignComponentCopyDialogFormValues) => void;
  isArchiveAvailable: boolean;
  isArchiveInProgress: boolean;
  onOfferArchive: () => void;
};

export const ComponentDetailsPage = ({
  campaign,
  offer,
  constraintSupplementalData,
  loading,
  initiallyLoading,
  isEditAvailable,
  isCopyAvailable,
  isCopyInProgress,
  onCopyDialogOpen,
  onCopyDialogClose,
  isCopyDialogOpen,
  onOfferCopy,
  error,
  isArchiveAvailable,
  isArchiveInProgress,
  onOfferArchive,
}: ComponentDetailsPageProps) => {
  const [openTriggerVisibilityRecalculationDialog, setOpenTriggerVisibilityRecalculationDialog] =
    useState<boolean>(false);
  const isUserPermittedToEditCampaignComponent = useIsUserPermittedTo(
    Feature.CAMPAIGN_COMPONENT_EDIT,
  );
  const isUserPermittedToViewCampaignComponentConfiguration = useIsUserPermittedTo(
    Feature.CAMPAIGN_COMPONENT_EDIT_VIEW_ONLY,
  );

  const links = useMemo(
    () =>
      getDetailsPageLinks(loading, !!error, campaign?.uuid, campaign?.name, offer?.id, offer?.name),
    [loading, error, campaign?.uuid, campaign?.name, offer?.id, offer?.name],
  );
  useAppLayoutContextConsumer(links);

  const {
    open: isArchiveDialogOpen,
    setOpen: setArchiveDialogOpen,
    onCloseButtonClick: onArchiveDialogClose,
    onConfirmButtonClick: onArchiveConfirm,
  } = useConfirmDialog({
    onConfirm: onOfferArchive,
    shouldStopEventPropagation: false,
  });

  const [triggerVisibilityNotifications] = useTriggerVisibilityNotificationsMutation({
    onCompleted: () => {
      snackbar.success('Recalculate visibility and send SMS triggered successfully');
    },
    onError: (err: ApolloError) => {
      snackbar.error(err.message);
    },
  });

  const initialValues: CampaignComponentCopyDialogFormValues = useMemo(() => {
    return {
      campaign: {
        uuid: campaign?.uuid ?? '',
        name: campaign?.name ?? '',
      },
    };
  }, [campaign?.uuid, campaign?.name]);

  if (error) {
    return <Typography color='error'>Something went wrong. Please try again.</Typography>;
  }

  if (initiallyLoading) {
    return <CircularProgress />;
  }

  if (!offer || !campaign) {
    return <NotFoundPage />;
  }

  if (!offer.engagementType) {
    return <Typography color='error'>Offer does not have engagementType</Typography>;
  }

  const type = getOfferComponentType(offer);
  const editUrl = resolveOfferEditUrl(offer);

  const closeConfirmationDialog = () => {
    setOpenTriggerVisibilityRecalculationDialog(false);
  };

  const onOpenTriggerVisibilityRecalculationDialog = () => {
    setOpenTriggerVisibilityRecalculationDialog(true);
  };

  const onConfirmTriggerVisibilityRecalculation = () => {
    triggerVisibilityNotifications({ variables: { input: { offerId: offer.id } } });
    closeConfirmationDialog();
  };

  const isPageActionsShown = type && !offer.deleted;
  const isLinkToOldOfferShown = isUserPermittedToEditCampaignComponent && !type;

  const actions = isPageActionsShown ? (
    <>
      {isUserPermittedToEditCampaignComponent && (
        <>
          {offer.allowedForManualVisibilityNotifications && (
            <Button
              color='secondary'
              variant='outlined'
              onClick={onOpenTriggerVisibilityRecalculationDialog}
              startIcon={<MessageIcon />}
            >
              Text updated audience
            </Button>
          )}
          {isEditAvailable && editUrl && (
            <Button
              color='secondary'
              variant='contained'
              component={RouterLink}
              to={editUrl}
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
          )}
          {isCopyAvailable && (
            <Button
              color='secondary'
              variant='contained'
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={onCopyDialogOpen}
              disabled={loading}
            >
              Copy
            </Button>
          )}
        </>
      )}
      {isArchiveAvailable && (
        <Button
          color='secondary'
          variant='contained'
          startIcon={<ArchiveIcon />}
          onClick={() => setArchiveDialogOpen(true)}
          disabled={isArchiveInProgress}
          role='button'
        >
          Archive
        </Button>
      )}
      {isUserPermittedToViewCampaignComponentConfiguration && editUrl && (
        <Button
          color='secondary'
          variant='contained'
          component={RouterLink}
          to={editUrl}
          startIcon={<VisibilityOutlinedIcon />}
        >
          View Configuration
        </Button>
      )}
    </>
  ) : null;

  return (
    <Page
      browserTabTitle='Campaign Component'
      pageHeaderProps={{ title: offer.name, subtitle: offer.description || undefined }}
      actions={actions}
    >
      {loading && <LinearProgress />}

      {isLinkToOldOfferShown && (
        <Box mb={2}>
          <OfferSupportAlert
            title='Campaign management does not yet support this configuration.'
            fallbackLink={`/offers/${offer.id}`}
            fallbackLinkTitle='View the original offer'
          />
        </Box>
      )}

      <ComponentDetails offer={offer} constraintSupplementalData={constraintSupplementalData} />

      <PromocodesSection
        offerType={type}
        offerId={offer.id}
        campaignUuid={campaign.uuid}
        earningMechanism={offer.earningMechanism}
        isActionsEnabled={isUserPermittedToEditCampaignComponent && !offer.deleted}
        isDownloadEnabled={
          isUserPermittedToEditCampaignComponent ||
          isUserPermittedToViewCampaignComponentConfiguration
        }
      />

      <CampaignComponentConfirmCopyDialog
        open={isCopyDialogOpen}
        onClose={onCopyDialogClose}
        loading={isCopyInProgress}
        componentName={offer.name}
        componentId={offer.id}
        initialValues={initialValues}
        onConfirm={onOfferCopy}
      />

      <ArchiveConfirmDialog
        open={isArchiveDialogOpen}
        componentName={offer.name}
        isArchiving={isArchiveInProgress}
        onClose={onArchiveDialogClose}
        onConfirm={onArchiveConfirm}
      />

      <ConfirmDialog
        open={openTriggerVisibilityRecalculationDialog}
        onClose={closeConfirmationDialog}
        confirmButtonText='SEND SMS'
        confirmButtonProps={{ onClick: onConfirmTriggerVisibilityRecalculation }}
      >
        <Typography>Do you want to recalculate visibility and send SMS?</Typography>
      </ConfirmDialog>
    </Page>
  );
};
